import * as m from '@staiter/i18n';
/**
 * FE-7 (tech-debt-carryover) — предпросмотр транзакционных email-шаблонов.
 * Раньше письма нельзя было посмотреть перед отправкой. Страница рендерит sample-версии
 * шаблонов (invite / сброс пароля / смена email / уведомление о задаче) в изолированном
 * iframe (srcdoc), показывает тему письма.
 *
 * Локали: шаблоны сейчас RU-only (`lang="ru"` в бэкенде). Селектор локали появится, когда
 * шаблоны будут локализованы — сейчас показываем явную пометку «RU».
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type JSX, useState } from 'react';
import { z } from 'zod';
import { PageHeader } from '~/components/data';
import { apiRequest } from '~/lib/api/client';

const EmailTemplatePreview = z.object({
  kind: z.string(),
  locale: z.string(),
  subject: z.string(),
  html: z.string(),
});
const PreviewResponse = z.object({
  templates: z.array(EmailTemplatePreview),
  locale_note: z.string(),
});

/**
 * D4.1 (CC-6) — подпись вида шаблона по коду. Раньше её присылал сервер готовой
 * русской строкой (`label`), и в en-локали раздел читался по-русски.
 */
const EMAIL_KIND_LABEL: Record<string, () => string> = {
  invite: m.admin_email_kind_invite,
  password_reset: m.admin_email_kind_password_reset,
  email_change: m.admin_email_kind_email_change,
  task_notification: m.admin_email_kind_task_notification,
};
const emailKindLabel = (kind: string): string => EMAIL_KIND_LABEL[kind]?.() ?? kind;

function EmailTemplatesPage(): JSX.Element {
  const q = useQuery({
    queryKey: ['admin', 'email-templates', 'preview'],
    queryFn: () => apiRequest('/admin/email-templates/preview', PreviewResponse),
  });
  const templates = q.data?.templates ?? [];
  const [selectedKind, setSelectedKind] = useState<string | null>(null);
  const selected = templates.find((t) => t.kind === selectedKind) ?? templates[0] ?? null;

  return (
    <div>
      <PageHeader
        title={m.admin_email_templates_title()}
        description={m.admin_email_templates_subtitle()}
      />

      {q.isLoading && <p>{m.ui_zagruzka()}</p>}
      {q.isError && <p style={{ color: 'var(--danger)' }}>{m.ui_ne_udalos_zagruzit_shablony()}</p>}

      {templates.length > 0 && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Список шаблонов */}
          <nav
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              minWidth: 220,
              flexShrink: 0,
            }}
            aria-label={m.admin_email_templates_list()}
          >
            {templates.map((t) => {
              const active = (selected?.kind ?? '') === t.kind;
              return (
                <button
                  key={t.kind}
                  type="button"
                  onClick={() => setSelectedKind(t.kind)}
                  style={{
                    textAlign: 'left',
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-sm, 6px)',
                    border: '1px solid',
                    borderColor: active ? 'var(--accent-strong)' : 'var(--border)',
                    background: active
                      ? 'var(--accent-bg-15, rgba(127,127,127,0.10))'
                      : 'transparent',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    font: 'inherit',
                    fontSize: '0.9rem',
                  }}
                >
                  {emailKindLabel(t.kind)}
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {t.locale}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Превью выбранного шаблона — центрированный столбец ~880px (design-review UI MINOR). */}
          {selected && (
            <div style={{ flex: 1, minWidth: 0, maxWidth: 880 }}>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {m.ui_tema_pisma()}
                </span>
                <p style={{ margin: '2px 0 0', fontWeight: 600 }}>{selected.subject}</p>
              </div>
              <iframe
                title={m.admin_email_preview_of({ name: emailKindLabel(selected.kind) })}
                srcDoc={selected.html}
                sandbox=""
                style={{
                  width: '100%',
                  height: 620,
                  border: '1px solid var(--border-strong, var(--border))',
                  borderRadius: 'var(--radius-md, 8px)',
                  background: '#fff',
                }}
              />
              <p style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {m.ui_lokal_ru_multiyazychnyy_predprosmotr_poyavitsya_posle_lok()}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_auth/admin/email-templates')({
  component: EmailTemplatesPage,
});
